/* ─── Real asset imports ────────────────────────────────────────────────────── */
import vinylRoseImg from '../assets/products/vinyl_rose/image.png';
import canvasCedarImg from '../assets/products/canvas_and_cedar/image.png';
import lavenderReverieImg from '../assets/products/lavender_reverie/product_img_01.png';
import lavenderReverieSecondaryImg from '../assets/products/lavender_reverie/product_img_02.png';
import vinylRoseBodyOilImg from '../assets/products/vinyl_rose_body_oil/product_img_01.png';
import vinylRoseBodyOilImg02 from '../assets/products/vinyl_rose_body_oil/product_img_02.png';
import micahPortraitMain from '../assets/people/micah/portrait_01.PNG';
import micahPortraitAbout from '../assets/people/micah/portrait_04_cropped.png';
import micahPortraitMusic from '../assets/people/micah/portrait_02.png';
import momAndDaughterBakingImg from '../assets/paintings/mom_and_daughter_baking/image.jpg';
import robotsHalloweenImg from '../assets/paintings/robots_halloween/image.jpg';
import sunroomImg from '../assets/paintings/sunroom/image.jpg';
import walkThroughNewOrleansImg from '../assets/paintings/walk_through_new_orleans/image.jpg';
import winnieThePoohImg from '../assets/paintings/winnie_the_pooh/image.jpg';
import robotDinnerImg from '../assets/paintings/robot_dinner/image.png';
import motelSixImg from '../assets/paintings/motel_six/motel_six.png';
import jokerPrintImg from '../assets/prints/joker/image.jpg';
import momDaughterBakingPrintImg from '../assets/prints/mom_daughter_baking/image.jpg';
import peacefulConvoPrintImg from '../assets/prints/peaceful_convo/image.jpg';
import robotsHalloweenPrintImg from '../assets/prints/robots_halloween/image.jpg';
import sanFranPrintImg from '../assets/prints/san_fran/image.jpg';
import sunroomPrintImg from '../assets/prints/sunroom/image.jpg';
import rainyDayNewOrleansPrintImg from '../assets/prints/rainy_day_new_orleans/image.png';
import rainyDayForestPrintImg from '../assets/prints/rainy_day_forest/image.png';
import winnieThePoohPrintImg from '../assets/prints/winnie_the_pooh/image.png';

export { vinylRoseImg, canvasCedarImg, lavenderReverieImg, lavenderReverieSecondaryImg, micahPortraitMain, micahPortraitAbout, micahPortraitMusic, vinylRoseBodyOilImg, vinylRoseBodyOilImg02, momAndDaughterBakingImg, robotsHalloweenImg, sunroomImg, walkThroughNewOrleansImg, winnieThePoohImg, robotDinnerImg, motelSixImg, jokerPrintImg, momDaughterBakingPrintImg, peacefulConvoPrintImg, robotsHalloweenPrintImg, sanFranPrintImg, sunroomPrintImg, rainyDayNewOrleansPrintImg, rainyDayForestPrintImg, winnieThePoohPrintImg };

/* ─────────────────────────────────────────────────────────────────────────────
   Placeholder image helper
   ───────────────────────────────────────────────────────────────────────────── */
export function placeholder(
  width: number,
  height: number,
  label = '',
  bg = '#EFE8DD',
  fg = '#B78D7A'
): string {
  const text = label || `${width}×${height}`;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <rect width="${width}" height="${height}" fill="${bg}"/>
      <text
        x="50%" y="50%"
        dominant-baseline="middle"
        text-anchor="middle"
        font-family="Georgia, serif"
        font-size="${Math.max(11, Math.min(18, width / 16))}px"
        fill="${fg}"
        opacity="0.6"
      >${text}</text>
    </svg>`.trim();
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Variant & Product types
   
   A ProductVariant represents one purchasable SKU.
   stripePriceId is filled in once you create products in the Stripe dashboard.
   Until then, leave as empty string — the checkout service will handle the
   missing-id case gracefully.
   ───────────────────────────────────────────────────────────────────────────── */
export interface ProductVariant {
  /** Display label shown in the size selector, e.g. "10 ml", "4 oz" */
  label: string;
  /** Machine value used as a key, e.g. "10ml", "4oz" */
  value: string;
  /** Stripe Price ID — fill in after creating prices in Stripe dashboard */
  stripePriceId: string;
  /** Price in cents, e.g. 2800 for $28.00 */
  priceCents: number;
  /** Human-readable price string */
  priceDisplay: string;
}

export interface Product {
  id: string;
  handle: string;
  title: string;
  subtitle: string;
  /** Display price — uses first variant's priceDisplay when variants exist */
  price: string;
  priceFrom?: boolean;
  category: 'fragrance' | 'body' | 'art' | 'music';
  type: string;
  scentFamily?: string;
  badge?: string;
  images: string[];
  /**
   * Ordered list of purchasable variants (size / format options).
   * Currently one variant per product; add more objects to this array
   * when new sizes become available — no other code changes needed.
   */
  variants: ProductVariant[];
}

/* ─── Helper: derive display price from variants ────────────────────────────── */
function displayPrice(variants: ProductVariant[]): string {
  if (variants.length === 0) return 'TBD';
  if (variants.length === 1) return variants[0].priceDisplay;
  const sorted = [...variants].sort((a, b) => a.priceCents - b.priceCents);
  return `From ${sorted[0].priceDisplay}`;
}

/* ─── Products ──────────────────────────────────────────────────────────────── */
const fragranceVariants10ml: ProductVariant[] = [
  {
    label: '10 ml',
    value: '10ml',
    stripePriceId: '', // TODO: fill in from Stripe dashboard
    priceCents: 2800,
    priceDisplay: '$28',
  },
  // To add a 30ml size later, append:
  // { label: '30 ml', value: '30ml', stripePriceId: '', priceCents: 5800, priceDisplay: '$58' },
];

const bodyButterVariants4oz: ProductVariant[] = [
  {
    label: '4 oz',
    value: '4oz',
    stripePriceId: '', // TODO: fill in from Stripe dashboard
    priceCents: 2000,
    priceDisplay: '$20',
  },
  // To add an 8oz size later, append:
  // { label: '8 oz', value: '8oz', stripePriceId: '', priceCents: 5800, priceDisplay: '$58' },
];

const bodyOilVariants60ml: ProductVariant[] = [
  {
    label: '60 ml',
    value: '60ml',
    stripePriceId: '', // TODO: fill in from Stripe dashboard
    priceCents: 2400,
    priceDisplay: '$24',
  },
  // To add a 120ml size later, append:
  // { label: '120 ml', value: '120ml', stripePriceId: '', priceCents: 6400, priceDisplay: '$64' },
];

const printVariantsBothSizes: ProductVariant[] = [
  {
    label: '11 × 14',
    value: '11x14-matted',
    stripePriceId: '',
    priceCents: 3500,
    priceDisplay: '$35',
  },
  {
    label: '16 × 20',
    value: '16x20-matted',
    stripePriceId: '',
    priceCents: 5500,
    priceDisplay: '$55',
  },
];

const printVariants11x14Only: ProductVariant[] = [
  {
    label: '11 × 14',
    value: '11x14-matted',
    stripePriceId: '',
    priceCents: 3500,
    priceDisplay: '$35',
  },
];

const printVariantsBothSizesHorizontal: ProductVariant[] = [
  {
    label: '14 × 11',
    value: '14x11-matted',
    stripePriceId: '',
    priceCents: 3500,
    priceDisplay: '$35',
  },
  {
    label: '20 × 16',
    value: '20x16-matted',
    stripePriceId: '',
    priceCents: 5500,
    priceDisplay: '$55',
  },
];

const originalPaintingVariant16x20: ProductVariant[] = [
  {
    label: '16 × 20',
    value: 'original',
    stripePriceId: '',
    priceCents: 1200 * 100, // Will be overridden by artwork price
    priceDisplay: '', // Will be set from artwork price
  },
];

export const products: Product[] = [
  {
    id: 'vinyl-rose-perfume-oil',
    handle: 'vinyl-rose-perfume-oil',
    title: 'Vinyl Rose',
    subtitle: 'A warm rose perfume oil softened by sandalwood, jasmine, and resin.',
    price: displayPrice(fragranceVariants10ml),
    category: 'fragrance',
    type: 'Natural Perfume Oil',
    scentFamily: 'Floral · Woody',
    variants: fragranceVariants10ml,
    images: [
      vinylRoseImg,
    ],
  },
  {
    id: 'canvas-and-cedar-cologne-oil',
    handle: 'canvas-and-cedar-cologne-oil',
    title: 'Canvas & Cedar',
    subtitle: 'A quiet walk through cedar woods after the rain. Bright bergamot gives way to soft rose before settling into warm Virginia cedarwood and Australian sandalwood.',
    price: displayPrice(fragranceVariants10ml),
    category: 'fragrance',
    type: 'Natural Cologne Oil',
    scentFamily: 'Woody · Fresh',
    variants: fragranceVariants10ml,
    images: [
      canvasCedarImg,
    ],
  },
  {
    id: 'lavender-reverie-body-butter',
    handle: 'lavender-reverie-body-butter',
    title: 'Lavender Reverie',
    subtitle: 'Organic whipped shea butter with coconut, jojoba, vitamin E, lavender and orange essential oils.',
    price: displayPrice(bodyButterVariants4oz),
    category: 'body',
    type: 'Organic Whipped Shea Butter',
    scentFamily: 'Lavender · Orange',
    variants: bodyButterVariants4oz,
    images: [
      lavenderReverieImg,
      lavenderReverieSecondaryImg
    ],
  },
  {
    id: 'vinyl-rose-body-oil',
    handle: 'vinyl-rose-body-oil',
    title: 'Vinyl Rose Body Oil',
    subtitle: 'A nourishing body oil in the Vinyl Rose scent.',
    price: displayPrice(bodyOilVariants60ml),
    category: 'body',
    type: 'Body Oil',
    scentFamily: 'Floral · Woody',
    variants: bodyOilVariants60ml,
    images: [vinylRoseBodyOilImg, vinylRoseBodyOilImg02],
  },
];

/* ─── Artworks ──────────────────────────────────────────────────────────────── */
export interface Artwork {
  id: string;
  handle: string;
  title: string;
  type: 'Original' | 'Print';
  medium?: string;
  dimensions?: string;
  year?: string;
  price: string;
  priceFrom?: boolean;
  available: boolean;
  images: string[];
  story?: string;
  variants?: ProductVariant[];
}

export const artworks: Artwork[] = [
  {
    id: 'joker-fine-art-print',
    handle: 'joker-fine-art-print',
    title: 'Joker',
    type: 'Print',
    price: displayPrice(printVariantsBothSizes),
    priceFrom: true,
    available: true,
    images: [jokerPrintImg],
    variants: printVariantsBothSizes,
  },
  {
    id: 'mom-and-daughter-baking-fine-art-print',
    handle: 'mom-and-daughter-baking-fine-art-print',
    title: 'Mom & Daughter Baking Christmas Rolls',
    type: 'Print',
    price: displayPrice(printVariants11x14Only),
    available: true,
    images: [momDaughterBakingPrintImg],
    variants: printVariants11x14Only,
  },
  {
    id: 'peaceful-conversation-fine-art-print',
    handle: 'peaceful-conversation-fine-art-print',
    title: 'Peaceful Conversation',
    type: 'Print',
    price: displayPrice(printVariantsBothSizes),
    priceFrom: true,
    available: true,
    images: [peacefulConvoPrintImg],
    variants: printVariantsBothSizes,
  },
  {
    id: 'robots-halloween-fine-art-print',
    handle: 'robots-halloween-fine-art-print',
    title: 'Father and Daughter Robots go Trick-or-Treating',
    type: 'Print',
    price: displayPrice(printVariantsBothSizes),
    priceFrom: true,
    available: true,
    images: [robotsHalloweenPrintImg],
    variants: printVariantsBothSizes,
  },
  {
    id: 'san-francisco-fine-art-print',
    handle: 'san-francisco-fine-art-print',
    title: 'San Francisco',
    type: 'Print',
    price: displayPrice(printVariants11x14Only),
    available: true,
    images: [sanFranPrintImg],
    variants: printVariants11x14Only,
  },
  {
    id: 'sunroom-fine-art-print',
    handle: 'sunroom-fine-art-print',
    title: 'Sunroom',
    type: 'Print',
    price: displayPrice(printVariantsBothSizes),
    priceFrom: true,
    available: true,
    images: [sunroomPrintImg],
    variants: printVariantsBothSizes,
  },
  {
    id: 'mom-and-daughter-baking-original',
    handle: 'mom-and-daughter-baking-original',
    title: 'Mom & Daughter Baking Christmas Rolls',
    type: 'Original',
    medium: 'Oil on Canvas',
    dimensions: '16 × 20 in',
    year: '2025',
    price: '$1,200',
    available: true,
    images: [momAndDaughterBakingImg],
    story: 'A tender moment of shared joy in the kitchen. Vibrant colors celebrating everyday intimacy and togetherness.',
    variants: [
      {
        label: '16 × 20',
        value: 'original',
        stripePriceId: '',
        priceCents: 120000,
        priceDisplay: '$1,200',
      },
    ],
  },
  {
    id: 'living-room-conversation-original',
    handle: 'living-room-conversation-original',
    title: 'Living Room Conversation',
    type: 'Original',
    medium: 'Oil on Canvas',
    dimensions: '16 × 20 in',
    year: '2026',
    price: '$800',
    available: true,
    images: [sunroomImg],
    story: 'Two women in conversation, framed by warm afternoon light. A moment of connection and reflection.',
    variants: [
      {
        label: '16 × 20',
        value: 'original',
        stripePriceId: '',
        priceCents: 80000,
        priceDisplay: '$800',
      },
    ],
  },
  {
    id: 'robots-halloween-original',
    handle: 'robots-halloween-original',
    title: 'Father and Daughter Robots go Trick-or-Treating',
    type: 'Original',
    medium: 'Oil on Canvas',
    dimensions: '16 × 20 in',
    year: '2025',
    price: '$800',
    available: true,
    images: [robotsHalloweenImg],
    story: 'A playful exploration of childhood imagination and festive celebration.',
    variants: [
      {
        label: '16 × 20',
        value: 'original',
        stripePriceId: '',
        priceCents: 80000,
        priceDisplay: '$800',
      },
    ],
  },
  {
    id: 'walk-through-new-orleans-original',
    handle: 'walk-through-new-orleans-original',
    title: 'A Rainy Day in New Orleans',
    type: 'Original',
    medium: 'Acrylic on Canvas',
    dimensions: '16 × 20 in',
    year: '2025',
    price: '$700',
    available: true,
    images: [walkThroughNewOrleansImg],
    story: 'A vibrant journey through the historic streets and soul of New Orleans.',
    variants: [
      {
        label: '16 × 20',
        value: 'original',
        stripePriceId: '',
        priceCents: 70000,
        priceDisplay: '$700',
      },
    ],
  },
  {
    id: 'my-childhood-friends-personified-original',
    handle: 'my-childhood-friends-personified-original',
    title: 'My Childhood Friends Personified',
    type: 'Original',
    medium: 'Oil on Canvas',
    dimensions: '16 × 20 in',
    year: '2025',
    price: '$800',
    available: true,
    images: [winnieThePoohImg],
    story: 'A nostalgic tribute to beloved childhood characters and timeless wonder.',
    variants: [
      {
        label: '16 × 20',
        value: 'original',
        stripePriceId: '',
        priceCents: 80000,
        priceDisplay: '$800',
      },
    ],
  },
  {
    id: 'rainy-day-new-orleans-fine-art-print',
    handle: 'rainy-day-new-orleans-fine-art-print',
    title: 'A Rainy Day in New Orleans',
    type: 'Print',
    price: displayPrice(printVariantsBothSizes),
    priceFrom: true,
    available: true,
    images: [rainyDayNewOrleansPrintImg],
    variants: printVariantsBothSizes,
  },
  {
    id: 'rainy-day-forest-fine-art-print',
    handle: 'rainy-day-forest-fine-art-print',
    title: 'Rainy Day Forest',
    type: 'Print',
    price: displayPrice(printVariants11x14Only),
    available: true,
    images: [rainyDayForestPrintImg],
    variants: printVariants11x14Only,
  },
  {
    id: 'winnie-the-pooh-fine-art-print',
    handle: 'winnie-the-pooh-fine-art-print',
    title: 'My Childhood Friends Personified',
    type: 'Print',
    price: displayPrice(printVariantsBothSizesHorizontal),
    priceFrom: true,
    available: true,
    images: [winnieThePoohPrintImg],
    variants: printVariantsBothSizesHorizontal,
  },
  {
    id: 'robot-dinner-original',
    handle: 'robot-dinner-original',
    title: 'Robot Dinner',
    type: 'Original',
    medium: 'Oil on Canvas',
    dimensions: '16 × 20 in',
    year: '2025',
    price: '$800',
    available: true,
    images: [robotDinnerImg],
    story: '',
    variants: [
      {
        label: '16 × 20',
        value: 'original',
        stripePriceId: '',
        priceCents: 80000,
        priceDisplay: '$800',
      },
    ],
  },
  {
    id: 'motel-six-original',
    handle: 'motel-six-original',
    title: 'Motel Six',
    type: 'Original',
    medium: 'Oil on Canvas',
    dimensions: '16 × 20 in',
    year: '2025',
    price: '$800',
    available: true,
    images: [motelSixImg],
    story: '',
    variants: [
      {
        label: '16 × 20',
        value: 'original',
        stripePriceId: '',
        priceCents: 80000,
        priceDisplay: '$800',
      },
    ],
  },
];

/* ─── Music ─────────────────────────────────────────────────────────────────── */
export interface Track {
  number: number;
  title: string;
  duration: string;
}

export interface Release {
  id: string;
  title: string;
  eyebrow: string;
  releaseType: string;
  trackCount: number;
  coverImage: string;
  tracks: Track[];
  note: string;
  stayTuned?: string;
  spotify?: string;
  appleMusic?: string;
  youtube?: string;
}

export const latestRelease: Release = {
  id: 'i-remember-when',
  title: 'I Remember When',
  eyebrow: 'Coming Soon',
  releaseType: 'EP · Coming Soon',
  trackCount: 0,
  coverImage: placeholder(300, 300, 'I Remember When', '#2A2520', '#B78D7A'),
  tracks: [],
  note: 'A debut EP following a personal journey through grief, acceptance, and personal growth. More details coming soon.',
  stayTuned: 'Follow along for updates on the release.',
};
